import { useEffect, useRef } from 'preact/hooks';
import type { ComponentProps } from 'preact';

// Base props for all chart types
interface D3ChartProps {
  data: any;
  width?: number;
  height?: number;
  margin?: { top: number; right: number; bottom: number; left: number };
}

// Funnel Chart Island Component
export function FunnelChartIsland({ 
  data, 
  width = 400, 
  height = 300,
  margin = { top: 20, right: 20, bottom: 30, left: 50 }
}: D3ChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (!chartRef.current || !data || typeof window === 'undefined' || !window.d3) return;
    
    // Clear previous chart
    const d3 = window.d3;
    d3.select(chartRef.current).selectAll('*').remove();
    
    // Calculate dimensions
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;
    
    // Create SVG
    const svg = d3.select(chartRef.current)
      .append('svg')
      .attr('width', width)
      .attr('height', height);
    
    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);
    
    // Funnel data processing
    const stages = data.stages || [];
    const maxValue = Math.max(...stages.map((d: any) => d.value));
    
    // Create funnel segments
    const segmentHeight = innerHeight / stages.length;
    
    stages.forEach((stage: any, i: number) => {
      const widthScale = stage.value / maxValue;
      const topWidth = innerWidth * (i === 0 ? 1 : stages[i - 1].value / maxValue);
      const bottomWidth = innerWidth * widthScale;
      const y = i * segmentHeight;
      
      // Create trapezoid path
      const trapezoid = [
        [(innerWidth - topWidth) / 2, y],
        [(innerWidth + topWidth) / 2, y],
        [(innerWidth + bottomWidth) / 2, y + segmentHeight],
        [(innerWidth - bottomWidth) / 2, y + segmentHeight]
      ];
      
      // Draw segment
      g.append('path')
        .attr('d', d3.line()(trapezoid) + 'Z')
        .attr('fill', d3.schemeCategory10[i % 10])
        .attr('stroke', '#fff')
        .attr('stroke-width', 2)
        .on('mouseover', function() {
          d3.select(this).attr('opacity', 0.8);
        })
        .on('mouseout', function() {
          d3.select(this).attr('opacity', 1);
        });
      
      // Add labels
      g.append('text')
        .attr('x', innerWidth / 2)
        .attr('y', y + segmentHeight / 2)
        .attr('text-anchor', 'middle')
        .attr('dy', '.35em')
        .attr('fill', '#fff')
        .style('font-weight', 'bold')
        .text(`${stage.name}: ${stage.value}`);
    });
  }, [data, width, height, margin]);
  
  return <div ref={chartRef} class="d3-chart funnel-chart"></div>;
}

// Sparkline Chart Island Component
export function SparklineChartIsland({ 
  data, 
  width = 100, 
  height = 30,
  color = '#3b82f6'
}: D3ChartProps & { color?: string }) {
  const chartRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (!chartRef.current || !data || typeof window === 'undefined' || !window.d3) return;
    
    const d3 = window.d3;
    d3.select(chartRef.current).selectAll('*').remove();
    
    const svg = d3.select(chartRef.current)
      .append('svg')
      .attr('width', width)
      .attr('height', height);
    
    // Scales
    const xScale = d3.scaleLinear()
      .domain([0, data.length - 1])
      .range([0, width]);
    
    const yScale = d3.scaleLinear()
      .domain(d3.extent(data))
      .range([height - 2, 2]);
    
    // Line generator
    const line = d3.line()
      .x((d: any, i: number) => xScale(i))
      .y((d: any) => yScale(d))
      .curve(d3.curveMonotoneX);
    
    // Draw line
    svg.append('path')
      .datum(data)
      .attr('fill', 'none')
      .attr('stroke', color)
      .attr('stroke-width', 2)
      .attr('d', line);
    
    // Add dots for last point
    if (data.length > 0) {
      svg.append('circle')
        .attr('cx', xScale(data.length - 1))
        .attr('cy', yScale(data[data.length - 1]))
        .attr('r', 3)
        .attr('fill', color);
    }
  }, [data, width, height, color]);
  
  return <div ref={chartRef} class="d3-chart sparkline-chart"></div>;
}

// Bar Chart Island Component
export function BarChartIsland({ 
  data, 
  width = 400, 
  height = 300,
  margin = { top: 20, right: 20, bottom: 40, left: 40 }
}: D3ChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (!chartRef.current || !data || typeof window === 'undefined' || !window.d3) return;
    
    const d3 = window.d3;
    d3.select(chartRef.current).selectAll('*').remove();
    
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;
    
    const svg = d3.select(chartRef.current)
      .append('svg')
      .attr('width', width)
      .attr('height', height);
    
    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);
    
    // Scales
    const xScale = d3.scaleBand()
      .domain(data.map((d: any) => d.name))
      .range([0, innerWidth])
      .padding(0.1);
    
    const yScale = d3.scaleLinear()
      .domain([0, d3.max(data, (d: any) => d.value)])
      .nice()
      .range([innerHeight, 0]);
    
    // Bars
    g.selectAll('.bar')
      .data(data)
      .enter().append('rect')
      .attr('class', 'bar')
      .attr('x', (d: any) => xScale(d.name))
      .attr('y', (d: any) => yScale(d.value))
      .attr('width', xScale.bandwidth())
      .attr('height', (d: any) => innerHeight - yScale(d.value))
      .attr('fill', '#3b82f6')
      .on('mouseover', function() {
        d3.select(this).attr('fill', '#2563eb');
      })
      .on('mouseout', function() {
        d3.select(this).attr('fill', '#3b82f6');
      });
    
    // X axis
    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale))
      .selectAll('text')
      .attr('transform', 'rotate(-45)')
      .style('text-anchor', 'end');
    
    // Y axis
    g.append('g')
      .call(d3.axisLeft(yScale).ticks(5));
  }, [data, width, height, margin]);
  
  return <div ref={chartRef} class="d3-chart bar-chart"></div>;
}

// Combined D3Chart Island that can render different chart types
export function D3ChartIsland({ 
  type = 'bar',
  ...props 
}: D3ChartProps & { type?: 'funnel' | 'sparkline' | 'bar' }) {
  switch (type) {
    case 'funnel':
      return <FunnelChartIsland {...props} />;
    case 'sparkline':
      return <SparklineChartIsland {...props} />;
    case 'bar':
    default:
      return <BarChartIsland {...props} />;
  }
}

// Declare d3 on window for TypeScript
declare global {
  interface Window {
    d3: any;
  }
}